// Header help icon: FAQ dropdown + a mailto: support link prefilled from the
// session and the Voltz version the orchestrator reports. Popover at sm+,
// bottom sheet at <sm (like the bell). FAQs are the const below - edit in place.

import { useQuery } from '@tanstack/react-query';
import { ChevronDown, HelpCircle, Mail } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Sheet } from '@/components/ui';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/classnames';
import { useIsMobile } from '@/lib/useMediaQuery';

const SUPPORT_EMAIL = 'contact@foresee-security.com';

type FAQ = { q: string; a: string };

const FAQS: FAQ[] = [
  {
    q: 'How do I submit a sample?',
    a: 'Open the Submit page, drop your file (or a password-protected zip) into the upload area, choose a duration, and click Run.',
  },
  {
    q: 'Where do I find my reports?',
    a: 'The Submissions page lists every batch you have launched. Click a row to open the full report: verdict, telemetry, and downloadable artifacts.',
  },
  {
    q: 'How long does an analysis take?',
    a: 'Most samples finish in three to ten minutes, depending on the duration you chose and the current queue.',
  },
];

export function HelpMenu() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const faqIdPrefix = useId();
  const version = useQuery({ queryKey: ['version'], queryFn: api.version, staleTime: Infinity }).data?.version;

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  // Popover-only outside-click + Esc. Sheet handles its own dismiss.
  useEffect(() => {
    if (!open || isMobile) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, isMobile, close]);

  const mailto = useMemo(() => {
    const subject = `Support request from ${user?.display_name ?? user?.email ?? 'user'}`;
    const body =
      `Hi Foresee team,\n\n` +
      `(Describe your question or issue here.)\n\n` +
      `---\n` +
      `Account: ${user?.email ?? '(unknown)'}\n` +
      (version ? `Version: ${version}\n` : '');
    return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [user?.display_name, user?.email, version]);

  const body = (
    <>
      <div className="divide-y divide-ink-800/60">
        {FAQS.map((f, i) => {
          const isOpen = expanded === i;
          const bodyId = `${faqIdPrefix}-faq-${i}`;
          return (
            <div key={f.q}>
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : i)}
                className="ring-focus w-full flex items-center justify-between gap-3 px-4 py-3 text-left text-sm text-ink-200 hover:bg-ink-800/60 transition-colors"
                aria-expanded={isOpen}
                aria-controls={bodyId}
              >
                <span>{f.q}</span>
                <ChevronDown
                  className={cn(
                    'w-3.5 h-3.5 text-ink-500 transition-transform shrink-0',
                    isOpen && 'rotate-180',
                  )}
                />
              </button>
              {isOpen && (
                <div
                  id={bodyId}
                  className="px-4 pb-3 text-xs text-ink-400 leading-relaxed"
                >
                  {f.a}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="px-4 py-3 border-t border-ink-800/60">
        <div className="eyebrow mb-2">Need more help?</div>
        <a href={mailto} className="btn-secondary w-full">
          <Mail className="w-3.5 h-3.5" />
          Contact support
        </a>
        {version && <div className="mt-2 text-xs text-ink-500">Voltz {version}</div>}
      </div>
    </>
  );

  return (
    <div className="relative" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="ring-focus relative inline-flex items-center justify-center w-10 h-10 rounded-md hover:bg-ink-900 transition-colors text-ink-400 hover:text-ink-100"
        aria-label="Help and support"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <HelpCircle className="w-4 h-4" />
      </button>

      {isMobile ? (
        <Sheet
          open={open}
          onOpenChange={setOpen}
          title="Help & support"
        >
          {body}
        </Sheet>
      ) : (
        open && (
          <div
            role="dialog"
            aria-label="Help and support"
            className="absolute right-0 mt-1.5 w-[min(20rem,calc(100vw-1.5rem))] card p-0 z-50 animate-slide-down overflow-hidden"
          >
            <div className="px-4 py-2.5 border-b border-ink-800/60">
              <div className="text-sm font-medium text-ink-100">Help & support</div>
            </div>
            {body}
          </div>
        )
      )}
    </div>
  );
}
